import apiService from 'services/api.service'

/**
 * Signing in as somebody else, from the portal's side.
 *
 * The original session is kept under its own keys rather than being thrown away, so
 * coming back does not need the password again. What it holds is a token the backend
 * already issued - nothing new is trusted here, and the backend still decides who may
 * do this to whom.
 *
 * The page is reloaded on both legs deliberately. Half this app reads the role and
 * token once, at mount, into places a dispatch does not reach; swapping them under a
 * live tree leaves menus, guards and listings disagreeing about who is signed in,
 * which is a worse failure than a second of white screen.
 */
const ORIGINAL = {
    token: 'impersonator_token',
    username: 'impersonator_username',
    role: 'impersonator_role',
}

export const isImpersonating = () =>
    Boolean(localStorage.getItem(ORIGINAL.token))

export const impersonatorName = () =>
    localStorage.getItem(ORIGINAL.username) || 'your account'

export async function loginAs(id) {
    const res = await apiService.impersonateUser({ id })
    const data = res?.data

    if (!data?.token) {
        throw new Error(res?.message || 'Could not sign in as this user.')
    }

    // Saved BEFORE the swap. If this ran after, a failure between the two would leave
    // the tab holding a borrowed session with no way back to its own.
    localStorage.setItem(ORIGINAL.token, localStorage.getItem('token') || '')
    localStorage.setItem(ORIGINAL.username, localStorage.getItem('username') || '')
    localStorage.setItem(ORIGINAL.role, localStorage.getItem('role') || '')

    localStorage.setItem('token', data.token)
    localStorage.setItem('username', data.username || '')
    localStorage.setItem('role', data.role || '')
    localStorage.setItem('user_id', data.id)

    window.location.href = '/'
}

export async function backToMyAccount() {
    try {
        const res = await apiService.stopImpersonating()
        const data = res?.data

        if (data?.token) {
            localStorage.setItem('token', data.token)
            localStorage.setItem('username', data.username || '')
            localStorage.setItem('role', data.role || '')
            localStorage.setItem('user_id', data.id)
        }
    } catch (e) {
        // The borrowed token may already be gone - expired, revoked, or the account
        // deactivated while it was being used. The saved session is still valid, and
        // stranding somebody in an account that is not theirs is the one outcome worth
        // avoiding, so fall back to it rather than reporting a failure.
        localStorage.setItem('token', localStorage.getItem(ORIGINAL.token) || '')
        localStorage.setItem('username', localStorage.getItem(ORIGINAL.username) || '')
        localStorage.setItem('role', localStorage.getItem(ORIGINAL.role) || '')
    }

    Object.values(ORIGINAL).forEach((key) => localStorage.removeItem(key))
    window.location.href = '/'
}
